import React from 'react';

/**
 * "אישור סעיף 46" block — a trust/credibility badge that tells donors the
 * organization is a recognized public institution (מוסד ציבורי מוכר) under
 * Section 46 of the Israeli Income Tax Ordinance, so their donation qualifies
 * for a tax credit.
 *
 * One block type, 10 hand-designed variations selected via `variant` (1–10).
 * Shared by the builder canvas (BlockRenderer) and the public page
 * (PublicCampaign) so both render byte-identically.
 */

export interface Tax46Props {
  /** 1–10 — which visual design to render. */
  variant?: number;
  /** Main headline, e.g. "מוסד מוכר לתרומות". */
  title?: string;
  /** Sub-line, e.g. "לפי סעיף 46 לפקודת מס הכנסה". */
  subtitle?: string;
  /** Big percentage, e.g. "35%". */
  percentText?: string;
  /** Caption beside/under the percentage, e.g. "החזר מס על תרומתך". */
  percentLabel?: string;
  /** Optional recognition / certificate number. */
  certNumber?: string;
  /** Optional organization name shown on certificate-style variants. */
  orgName?: string;
  /** Primary accent colour (seal, highlights). */
  accentColor?: string;
  /** Card / surface background colour. */
  bgColor?: string;
  /** Main text colour. */
  textColor?: string;
  /** Show the percentage highlight where the variant supports it. */
  showPercent?: boolean;
  /** Show the certificate number line where the variant supports it. */
  showCert?: boolean;
}

export const TAX46_VARIANT_COUNT = 15;

export const TAX46_DEFAULTS: Required<Omit<Tax46Props, 'variant'>> & { variant: number } = {
  variant: 1,
  title: 'מוסד מוכר לתרומות',
  subtitle: 'לפי סעיף 46 לפקודת מס הכנסה',
  percentText: '35%',
  percentLabel: 'החזר מס על תרומתך',
  certNumber: '',
  orgName: '',
  accentColor: '#1d4ed8',
  bgColor: '#ffffff',
  textColor: '#0f172a',
  showPercent: true,
  showCert: false,
};

/** Resolve props against defaults so renderers can pass a sparse object. */
function resolve(p: Tax46Props): Required<Tax46Props> {
  return {
    variant: clampVariant(p.variant),
    title: p.title ?? TAX46_DEFAULTS.title,
    subtitle: p.subtitle ?? TAX46_DEFAULTS.subtitle,
    percentText: p.percentText ?? TAX46_DEFAULTS.percentText,
    percentLabel: p.percentLabel ?? TAX46_DEFAULTS.percentLabel,
    certNumber: p.certNumber ?? TAX46_DEFAULTS.certNumber,
    orgName: p.orgName ?? TAX46_DEFAULTS.orgName,
    accentColor: p.accentColor ?? TAX46_DEFAULTS.accentColor,
    bgColor: p.bgColor ?? TAX46_DEFAULTS.bgColor,
    textColor: p.textColor ?? TAX46_DEFAULTS.textColor,
    showPercent: p.showPercent !== false,
    showCert: p.showCert === true,
  };
}

/** Variants 11–15 are single-line; they auto-size to their content height
 *  instead of filling the authored box, so they don't leave a tall gap below.
 *  Card variants (1–10) keep their authored box so the designer controls size. */
export function isCompactTax46Variant(variant: number | undefined): boolean {
  return clampVariant(variant) >= 11;
}

/** True for a `tax-46` block whose selected variant is a single-line one. */
export function isCompactTax46Block(b: { type?: string; props?: { variant?: number } } | null | undefined): boolean {
  return !!b && b.type === 'tax-46' && isCompactTax46Variant(b.props?.variant);
}

export function clampVariant(v: number | undefined): number {
  const n = Math.round(Number(v) || 1);
  if (n < 1) return 1;
  if (n > TAX46_VARIANT_COUNT) return TAX46_VARIANT_COUNT;
  return n;
}

export const TAX46_VARIANT_LABELS: Record<number, string> = {
  1: 'תו אמון',
  2: 'רצועת אישור',
  3: 'תעודה רשמית',
  4: 'חותמת מאושר',
  5: 'דגש החזר מס',
  6: 'פיל מינימלי',
  7: 'כרטיס גרדיאנט',
  8: 'מפוצל עם קו',
  9: 'באנר עם סימוני וי',
  10: 'מדליית זהב',
  11: 'שורה מינימלית',
  12: 'רצועה דקה',
  13: 'קו תחתון',
  14: 'תגית מלאה',
  15: 'שורה עם נקודות',
};

/* ---------- shared SVG glyphs ---------- */

const ShieldCheck: React.FC<{ size?: number; color?: string }> = ({ size = 28, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 2 4 5v6c0 4.4 3.1 8.4 8 11 4.9-2.6 8-6.6 8-11V5l-8-3Z" fill={color} fillOpacity={0.18} stroke={color} strokeWidth={1.6} strokeLinejoin="round" />
    <path d="m8.5 12 2.4 2.4L16 9.3" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SealCheck: React.FC<{ size?: number; color?: string; bg?: string }> = ({ size = 56, color = '#fff', bg = '#1d4ed8' }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden>
    <g>
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * 360) / 16;
        return <rect key={i} x={31} y={1} width={2} height={8} rx={1} fill={bg} transform={`rotate(${a} 32 32)`} />;
      })}
      <circle cx={32} cy={32} r={24} fill={bg} />
      <circle cx={32} cy={32} r={24} fill="none" stroke={color} strokeOpacity={0.45} strokeWidth={1.5} strokeDasharray="2 3" />
      <path d="m22 33 6.5 6.5L43 25" stroke={color} strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
  </svg>
);

const fontStack = 'inherit';

/* ---------- the block ---------- */

export const Tax46Block: React.FC<{ props?: Tax46Props; scale?: number; fontFamily?: string; fontSize?: number }> = ({ props = {}, scale = 1, fontFamily, fontSize }) => {
  const c = resolve(props);
  // "גודל פונט" from the design panel scales EVERY text size in the badge.
  // The control's default value is 16, so 16 (or unset) means no change (×1);
  // 24 → ×1.5, etc. Every literal fontSize below is wrapped in fz() so the
  // whole badge's typography grows/shrinks proportionally.
  const fontScale = fontSize && fontSize > 0 ? fontSize / 16 : 1;
  const fz = (n: number) => Math.round(n * fontScale * 100) / 100;
  const wrap: React.CSSProperties = {
    width: '100%', height: '100%', boxSizing: 'border-box',
    // Honour the block's chosen font explicitly. Relying on `inherit` alone
    // proved unreliable across the nested builder/public wrappers, so when the
    // designer picks a font we apply it here; otherwise fall back to inherit.
    fontFamily: fontFamily || fontStack, direction: 'rtl',
    display: 'flex',
  };

  switch (c.variant) {
    /* 1 — תו אמון: clean white card, seal + text */
    case 1:
      return (
        <div style={{ ...wrap, alignItems: 'center', gap: 16, background: c.bgColor, borderRadius: 16,
          border: '1px solid #e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 18 }}>
          <SealCheck size={54} bg={c.accentColor} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <div style={{ fontSize: fz(17), fontWeight: 800, color: c.textColor, lineHeight: 1.2 }}>{c.title}</div>
            <div style={{ fontSize: fz(13), color: '#64748b' }}>{c.subtitle}</div>
            {c.showPercent && (
              <div style={{ fontSize: fz(12.5), color: c.accentColor, fontWeight: 700, marginTop: 2 }}>
                ✓ {c.percentLabel} · {c.percentText}
              </div>
            )}
            {c.showCert && c.certNumber && <div style={{ fontSize: fz(11), color: '#94a3b8' }}>אישור מס׳ {c.certNumber}</div>}
          </div>
        </div>
      );

    /* 2 — רצועת אישור: gradient ribbon strip with percent pill */
    case 2:
      return (
        <div style={{ ...wrap, alignItems: 'center', gap: 12, borderRadius: 12, padding: '14px 18px', color: '#fff',
          background: `linear-gradient(110deg, ${c.accentColor}, ${shade(c.accentColor, -22)})` }}>
          <ShieldCheck size={30} color="#fff" />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: fz(16), fontWeight: 800, lineHeight: 1.2 }}>{c.title}</div>
            <div style={{ fontSize: fz(12.5), opacity: 0.9 }}>{c.subtitle}</div>
          </div>
          {c.showPercent && (
            <div style={{ background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.45)', borderRadius: 999,
              padding: '6px 14px', textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: fz(18), fontWeight: 900, lineHeight: 1 }}>{c.percentText}</div>
              <div style={{ fontSize: fz(9.5), opacity: 0.9, marginTop: 2 }}>החזר מס</div>
            </div>
          )}
        </div>
      );

    /* 3 — תעודה רשמית: framed certificate look */
    case 3:
      return (
        <div style={{ ...wrap, padding: 6, background: '#fbfaf7', borderRadius: 8 }}>
          <div style={{ flex: 1, border: `2px solid ${c.accentColor}`, borderRadius: 6, padding: 16, position: 'relative',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, textAlign: 'center',
            boxShadow: `inset 0 0 0 4px #fbfaf7, inset 0 0 0 5px ${hexA(c.accentColor, 0.35)}` }}>
            <SealCheck size={46} bg={c.accentColor} />
            <div style={{ fontSize: fz(16), fontWeight: 800, color: c.textColor, letterSpacing: 0.2 }}>{c.title}</div>
            <div style={{ fontSize: fz(12.5), color: '#6b7280' }}>{c.subtitle}</div>
            {c.orgName && <div style={{ fontSize: fz(12), color: c.textColor, fontWeight: 600 }}>{c.orgName}</div>}
            {c.showCert && c.certNumber && (
              <div style={{ fontSize: fz(10.5), color: '#9ca3af', marginTop: 2, borderTop: '1px dashed #d6d3cd', paddingTop: 6, width: '70%' }}>
                אישור מוסד ציבורי מס׳ {c.certNumber}
              </div>
            )}
          </div>
        </div>
      );

    /* 4 — חותמת מאושר: rotated rubber stamp */
    case 4:
      return (
        <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center', background: c.bgColor === '#ffffff' ? 'transparent' : c.bgColor }}>
          <div style={{ transform: 'rotate(-9deg)', border: `3px double ${c.accentColor}`, color: c.accentColor,
            borderRadius: 12, padding: '12px 22px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            background: hexA(c.accentColor, 0.04) }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={22} color={c.accentColor} />
              <span style={{ fontSize: fz(20), fontWeight: 900, letterSpacing: 2 }}>מאושר</span>
            </div>
            <div style={{ fontSize: fz(13), fontWeight: 800, letterSpacing: 1 }}>{c.title}</div>
            <div style={{ fontSize: fz(10.5), fontWeight: 700, opacity: 0.85 }}>{c.subtitle}</div>
          </div>
        </div>
      );

    /* 5 — דגש החזר מס: big percent number focus */
    case 5:
      return (
        <div style={{ ...wrap, alignItems: 'stretch', gap: 0, borderRadius: 16, overflow: 'hidden',
          border: '1px solid #e5e7eb', background: c.bgColor }}>
          <div style={{ background: c.accentColor, color: '#fff', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: '0 22px', minWidth: 120 }}>
            <div style={{ fontSize: fz(40), fontWeight: 900, lineHeight: 1 }}>{c.percentText}</div>
            <div style={{ fontSize: fz(11), opacity: 0.92, marginTop: 4 }}>החזר מס</div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, padding: '14px 18px' }}>
            <div style={{ fontSize: fz(16), fontWeight: 800, color: c.textColor }}>{c.percentLabel}</div>
            <div style={{ fontSize: fz(13), color: '#64748b' }}>{c.title} · {c.subtitle}</div>
            {c.showCert && c.certNumber && <div style={{ fontSize: fz(11), color: '#94a3b8' }}>אישור מס׳ {c.certNumber}</div>}
          </div>
        </div>
      );

    /* 6 — פיל מינימלי: subtle inline pill */
    case 6:
      return (
        <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: hexA(c.accentColor, 0.1),
            border: `1px solid ${hexA(c.accentColor, 0.35)}`, color: c.accentColor, borderRadius: 999, padding: '9px 16px', maxWidth: '100%' }}>
            <ShieldCheck size={18} color={c.accentColor} />
            <span style={{ fontSize: fz(13.5), fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {c.title} {c.subtitle ? `· ${c.subtitle}` : ''}
            </span>
          </div>
        </div>
      );

    /* 7 — כרטיס גרדיאנט: colorful hero card */
    case 7:
      return (
        <div style={{ ...wrap, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, textAlign: 'center',
          borderRadius: 18, padding: 18, color: '#fff',
          background: `radial-gradient(120% 120% at 80% 0%, ${shade(c.accentColor, 18)}, ${shade(c.accentColor, -26)})`,
          boxShadow: `0 10px 30px ${hexA(c.accentColor, 0.35)}` }}>
          <SealCheck size={48} bg="rgba(255,255,255,.16)" color="#fff" />
          <div style={{ fontSize: fz(18), fontWeight: 800, lineHeight: 1.2 }}>{c.title}</div>
          <div style={{ fontSize: fz(12.5), opacity: 0.9 }}>{c.subtitle}</div>
          {c.showPercent && (
            <div style={{ fontSize: fz(13), fontWeight: 700, background: 'rgba(255,255,255,.18)', borderRadius: 999, padding: '5px 14px', marginTop: 2 }}>
              {c.percentLabel} — {c.percentText}
            </div>
          )}
        </div>
      );

    /* 8 — מפוצל עם קו: seal | divider | text+percent */
    case 8:
      return (
        <div style={{ ...wrap, alignItems: 'center', gap: 16, background: c.bgColor, borderRadius: 14,
          border: '1px solid #e5e7eb', padding: '16px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <SealCheck size={44} bg={c.accentColor} />
            <div style={{ fontSize: fz(10.5), fontWeight: 800, color: c.accentColor, letterSpacing: 1 }}>סעיף 46</div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: '#e5e7eb' }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <div style={{ fontSize: fz(16), fontWeight: 800, color: c.textColor }}>{c.title}</div>
            <div style={{ fontSize: fz(12.5), color: '#64748b' }}>{c.subtitle}</div>
            {c.showPercent && (
              <div style={{ fontSize: fz(13), color: c.textColor }}>
                <b style={{ color: c.accentColor, fontWeight: 900 }}>{c.percentText}</b> {c.percentLabel}
              </div>
            )}
          </div>
        </div>
      );

    /* 9 — באנר עם סימוני וי: bar with check bullets */
    case 9: {
      const bullets = [
        c.subtitle,
        c.showPercent ? `${c.percentLabel} ${c.percentText}` : '',
        'קבלה רשמית מוכרת למס',
      ].filter(Boolean);
      return (
        <div style={{ ...wrap, flexDirection: 'column', justifyContent: 'center', gap: 10, background: c.bgColor,
          borderRadius: 14, border: `1px solid ${hexA(c.accentColor, 0.25)}`, padding: '14px 18px',
          borderRight: `4px solid ${c.accentColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldCheck size={22} color={c.accentColor} />
            <span style={{ fontSize: fz(16), fontWeight: 800, color: c.textColor }}>{c.title}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
            {bullets.map((b, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: fz(12.5), color: '#475569' }}>
                <CheckDot color={c.accentColor} /> {b}
              </span>
            ))}
          </div>
        </div>
      );
    }

    /* 10 — מדליית זהב: premium gold medallion */
    case 10:
      return (
        <div style={{ ...wrap, alignItems: 'center', gap: 18, background: 'linear-gradient(180deg,#fffdf5,#fbf6e7)',
          borderRadius: 16, border: '1px solid #efe3bf', padding: 18 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #fde68a, #d4a017 70%, #a9790c)',
              boxShadow: '0 4px 12px rgba(169,121,12,.35), inset 0 0 0 3px rgba(255,255,255,.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#5b3d05' }}>
              <span style={{ fontSize: fz(10), fontWeight: 800, letterSpacing: 0.5, opacity: 0.85 }}>סעיף</span>
              <span style={{ fontSize: fz(22), fontWeight: 900, lineHeight: 1 }}>46</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <div style={{ fontSize: fz(17), fontWeight: 800, color: '#5b3d05' }}>{c.title}</div>
            <div style={{ fontSize: fz(12.5), color: '#8a6d2f' }}>{c.subtitle}</div>
            {c.showPercent && (
              <div style={{ fontSize: fz(12.5), color: '#a9790c', fontWeight: 700, marginTop: 2 }}>
                ★ {c.percentLabel} · {c.percentText}
              </div>
            )}
          </div>
        </div>
      );

    /* 11 — שורה מינימלית: single transparent line, no surface */
    case 11:
      return (
        <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <ShieldCheck size={18} color={c.accentColor} />
          <span style={oneLine(c.textColor, fz(14), 700)}>
            תרומה מזכה בזיכוי מס לפי {c.subtitle}{c.showPercent ? ` (${c.percentText})` : ''}
          </span>
        </div>
      );

    /* 12 — רצועה דקה: slim full-width tinted bar */
    case 12:
      return (
        <div style={{ ...wrap, alignItems: 'center', gap: 10, background: hexA(c.accentColor, 0.1),
          borderRadius: 8, padding: '8px 14px' }}>
          <ShieldCheck size={16} color={c.accentColor} />
          <span style={{ ...oneLine(c.accentColor, fz(13), 700), flex: 1 }}>{c.title} · {c.subtitle}</span>
          {c.showPercent && <span style={{ fontSize: fz(12.5), fontWeight: 800, color: c.accentColor, flexShrink: 0 }}>{c.percentText} החזר מס</span>}
        </div>
      );

    /* 13 — קו תחתון: one line with an accent underline */
    case 13:
      return (
        <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, borderBottom: `2px solid ${c.accentColor}`,
            padding: '6px 4px', maxWidth: '100%' }}>
            <CheckDot color={c.accentColor} />
            <span style={oneLine(c.textColor, fz(13.5), 700)}>
              {c.title} · {c.subtitle}{c.showPercent ? ` · ${c.percentText}` : ''}
            </span>
          </div>
        </div>
      );

    /* 14 — תגית מלאה: solid accent pill, one line */
    case 14:
      return (
        <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: c.accentColor, color: '#fff',
            borderRadius: 999, padding: '8px 16px', maxWidth: '100%' }}>
            <ShieldCheck size={16} color="#fff" />
            <span style={oneLine('#fff', fz(13), 700)}>
              מוכר לתרומות · סעיף 46{c.showPercent ? ` · ${c.percentText}` : ''}
            </span>
          </div>
        </div>
      );

    /* 15 — שורה עם נקודות: centered dot-separated line */
    case 15:
      return (
        <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <CheckDot color={c.accentColor} />
          <span style={oneLine(c.textColor, fz(13.5), 400)}>
            <b style={{ fontWeight: 800 }}>{c.title}</b> · {c.subtitle}{c.showPercent ? ` · החזר מס ${c.percentText}` : ''}
          </span>
        </div>
      );

    default:
      return null;
  }
};

/** One-line ellipsised text style shared by the compact variants. */
function oneLine(color: string, fontSize: number, fontWeight: number): React.CSSProperties {
  return { fontSize, fontWeight, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
}

const CheckDot: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%',
    background: hexA(color, 0.15), color, fontSize: 10, fontWeight: 900, flexShrink: 0 }}>✓</span>
);

/* ---------- colour helpers (no deps) ---------- */

function hexA(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function shade(hex: string, percent: number): string {
  const { r, g, b } = parseHex(hex);
  const t = percent < 0 ? 0 : 255;
  const f = Math.abs(percent) / 100;
  const mix = (ch: number) => Math.round((t - ch) * f + ch);
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  let h = (hex || '#1d4ed8').replace('#', '');
  if (h.length === 3) h = h.split('').map(x => x + x).join('');
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return { r: 29, g: 78, b: 216 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
